import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, ArrowRight, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, digits) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);

export const GoldRate = () => {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () =>
      axios
        .get(`${API}/gold-price`)
        .then((r) => {
          if (alive) {
            setQuote(r.data);
            setError(false);
          }
        })
        .catch(() => {
          if (alive) setError(true);
        });
    load();
    const timer = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  return (
    <section data-testid="gold-rate-band" className="border-y border-[#E8DCC4] bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-14">
            <div className="flex items-center gap-4 shrink-0">
              <span className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F0D988] to-[#C9A227] flex items-center justify-center text-neutral-900">
                <TrendingUp size={22} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold">Live Market</p>
                <h2 className="font-display text-2xl text-neutral-900">Today's Gold Rate</h2>
              </div>
            </div>

            {quote && !error && (
              <div className="flex flex-wrap items-baseline gap-x-12 gap-y-4" data-testid="gold-rate-values">
                <div>
                  <p className="font-display text-4xl md:text-5xl text-neutral-900" data-testid="gold-rate-oz">
                    {fmt(quote.price_per_troy_ounce, 2)}
                    <span className="text-lg text-neutral-500 font-sans ml-2">/ troy oz</span>
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl md:text-4xl text-neutral-700" data-testid="gold-rate-gram">
                    {fmt(quote.price_per_gram, 2)}
                    <span className="text-base text-neutral-500 font-sans ml-2">/ gram (24k)</span>
                  </p>
                </div>
                <p className="text-xs text-neutral-500 flex items-center gap-1.5" data-testid="gold-rate-updated">
                  <RefreshCw size={12} strokeWidth={1.5} />
                  {quote.cached ? "Updated moments ago" : "Just updated"} &middot; XAU/USD spot, indicative only
                </p>
              </div>
            )}

            {!quote && !error && (
              <p className="text-sm text-neutral-500" data-testid="gold-rate-loading">Fetching today's rate...</p>
            )}

            {error && (
              <p className="text-sm text-neutral-600" data-testid="gold-rate-error">
                Live rate temporarily unavailable — call us for today's best price.
              </p>
            )}

            <Link
              to="/sell"
              data-testid="gold-rate-cta"
              className="lg:ml-auto inline-flex items-center gap-2 text-sm font-semibold text-[#C9A227] hover:text-[#A8871B] transition-colors duration-300"
            >
              See what your gold is worth <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
